import { FormEvent, useEffect, useRef, useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Minus, Plus, RotateCcw, RotateCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Select } from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** POST target for new sheets — owner-aware, built by the picker page. */
  sheetsUrl: string
  planSetId: number
  pageNumber: number
  thumbUrl: string | null
  /** On-demand hi-res render (2400px longest side) — fetched only once crop
      intent shows, since the server renders it on the first request. */
  previewUrl?: string | null
  /** When set, the dialog edits this existing sheet instead of promoting. */
  existing?: { id: number; label: string; rotation: number }
  /** Composite sheets a cropped region can be added to instead of a new sheet. */
  compositeSheets?: { id: number; label: string }[]
  /** Community-owned sets promote INTO a library plan — the master's sheets. */
  plans?: { id: number; display_name: string }[]
}

// Promote a PDF page to a labeled sheet (or rename/rotate an existing one).
// The thumbnail preview rotates live with CSS — CSS clockwise degrees match
// the pipeline's clockwise rotation exactly, so what you see is what you get.
export function PromoteSheetDialog({
  open,
  onOpenChange,
  sheetsUrl,
  planSetId,
  pageNumber,
  thumbUrl,
  previewUrl = null,
  existing,
  compositeSheets = [],
  plans = [],
}: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const [label, setLabel] = useState(existing?.label ?? "")
  const [rotation, setRotation] = useState(existing?.rotation ?? 0)
  const [busy, setBusy] = useState(false)

  // Opt-in crop: a rect dragged over the (rotated) preview, stored as
  // FRACTIONS of the rotated raster. getBoundingClientRect of the CSS-rotated
  // img is exactly the rotated raster's screen box, so fractions measured
  // against it apply 1:1 to the rotated 300-dpi render.
  const [cropping, setCropping] = useState(false)
  const [crop, setCrop] = useState<{ x: number; y: number; w: number; h: number } | null>(null)
  // "new" = the crop becomes its own composite sheet; an id = add the crop
  // to that existing composite sheet.
  const [destination, setDestination] = useState<"new" | number>("new")
  const [planId, setPlanId] = useState<number | "">(plans.length === 1 ? plans[0].id : "")
  const containerRef = useRef<HTMLDivElement | null>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const dragOrigin = useRef<{ fx: number; fy: number } | null>(null)

  // Zoom/pan over the preview while cropping. The crop math never sees it:
  // fractions are measured against the img's getBoundingClientRect, which
  // tracks the wrapper's transform automatically.
  const [view, setView] = useState({ s: 1, tx: 0, ty: 0 })
  const panOrigin = useRef<{ x: number; y: number } | null>(null)
  const fitView = () => setView({ s: 1, tx: 0, ty: 0 })

  // Displayed source: the instant 480px picker thumb, swapped for the
  // on-demand hi-res render once it arrives (the server renders it on the
  // first request — a couple of seconds on a cold page, cached after).
  const [src, setSrc] = useState(thumbUrl)

  // A different page target → back to its own thumbnail, fitted.
  useEffect(() => {
    setSrc(thumbUrl)
    setView({ s: 1, tx: 0, ty: 0 })
  }, [thumbUrl])

  // The thumb hasn't the pixels to zoom into, so crop intent triggers the
  // hi-res fetch. A failure (pipeline missing, stale set) just leaves the
  // thumbnail — exactly the pre-preview behavior.
  useEffect(() => {
    if (!cropping || !previewUrl || src === previewUrl) return
    let alive = true
    const image = new Image()
    image.onload = () => {
      if (alive) setSrc(previewUrl)
    }
    image.src = previewUrl
    return () => {
      alive = false
    }
  }, [cropping, previewUrl, src])

  // Keep the image reachable: pan room grows with zoom, near-zero at fit.
  const clampView = (v: { s: number; tx: number; ty: number }) => {
    const el = containerRef.current
    if (!el) return v
    const limit = (dim: number) => ((v.s - 1) / 2) * dim + 40
    return {
      s: v.s,
      tx: Math.max(-limit(el.clientWidth), Math.min(limit(el.clientWidth), v.tx)),
      ty: Math.max(-limit(el.clientHeight), Math.min(limit(el.clientHeight), v.ty)),
    }
  }

  const zoomAt = (clientX: number, clientY: number, factor: number) =>
    setView((v) => {
      const el = containerRef.current
      if (!el) return v
      const b = el.getBoundingClientRect()
      const px = clientX - (b.left + b.width / 2)
      const py = clientY - (b.top + b.height / 2)
      const s = Math.min(8, Math.max(1, v.s * factor))
      if (s === v.s) return v
      const k = s / v.s
      return clampView({ s, tx: px - (px - v.tx) * k, ty: py - (py - v.ty) * k })
    })

  const zoomAboutCenter = (factor: number) => {
    const b = containerRef.current?.getBoundingClientRect()
    if (b) zoomAt(b.left + b.width / 2, b.top + b.height / 2, factor)
  }

  // Native non-passive wheel listener: React's synthetic onWheel can't
  // preventDefault (the root listener is passive), and without it the modal
  // behind the preview scrolls. Grammar matches the editor's canvas plus
  // trackpads: pinch / ⌘-or-Ctrl-scroll zooms at the cursor, plain scroll
  // pans (two-finger trackpad panning falls out naturally).
  useEffect(() => {
    const el = containerRef.current
    if (!el || !cropping) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      if (e.ctrlKey || e.metaKey) {
        zoomAt(e.clientX, e.clientY, Math.pow(1.0035, -e.deltaY))
      } else {
        setView((v) => clampView({ ...v, tx: v.tx - e.deltaX, ty: v.ty - e.deltaY }))
      }
    }
    el.addEventListener("wheel", onWheel, { passive: false })
    return () => el.removeEventListener("wheel", onWheel)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers read refs + setState only
  }, [cropping])

  const rotationChanged = existing != null && rotation !== existing.rotation

  const fracFromEvent = (e: React.PointerEvent) => {
    const bbox = imgRef.current!.getBoundingClientRect()
    return {
      fx: Math.min(1, Math.max(0, (e.clientX - bbox.left) / bbox.width)),
      fy: Math.min(1, Math.max(0, (e.clientY - bbox.top) / bbox.height)),
    }
  }

  // Left-drag draws the crop box; middle-drag pans (the editor's grammar).
  const onSurfacePointerDown = (e: React.PointerEvent) => {
    if (e.button === 1) {
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      panOrigin.current = { x: e.clientX, y: e.clientY }
      return
    }
    if (e.button !== 0 || !cropping || !imgRef.current) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    dragOrigin.current = fracFromEvent(e)
    setCrop(null)
  }

  const onSurfacePointerMove = (e: React.PointerEvent) => {
    if (panOrigin.current) {
      const dx = e.clientX - panOrigin.current.x
      const dy = e.clientY - panOrigin.current.y
      panOrigin.current = { x: e.clientX, y: e.clientY }
      setView((v) => clampView({ ...v, tx: v.tx + dx, ty: v.ty + dy }))
      return
    }
    if (!dragOrigin.current) return
    const { fx, fy } = fracFromEvent(e)
    const o = dragOrigin.current
    setCrop({
      x: Math.min(o.fx, fx),
      y: Math.min(o.fy, fy),
      w: Math.abs(fx - o.fx),
      h: Math.abs(fy - o.fy),
    })
  }

  const onSurfacePointerUp = () => {
    panOrigin.current = null
    if (!dragOrigin.current) return
    dragOrigin.current = null
    // Discard accidental clicks / degenerate slivers.
    setCrop((c) => (c && c.w >= 0.02 && c.h >= 0.02 ? c : null))
  }

  const cropSelectionStyle = () => {
    if (!crop || !imgRef.current || !containerRef.current) return null
    const ib = imgRef.current.getBoundingClientRect()
    const cb = containerRef.current.getBoundingClientRect()
    return {
      left: ib.left - cb.left + crop.x * ib.width,
      top: ib.top - cb.top + crop.y * ib.height,
      width: crop.w * ib.width,
      height: crop.h * ib.height,
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    const done = { onFinish: () => setBusy(false), onSuccess: () => onOpenChange(false) }
    if (existing) {
      router.patch(`/sheets/${existing.id}`, { label, rotation }, done)
    } else if (cropping && crop && destination !== "new") {
      router.post(
        `/sheets/${destination}/images`,
        { plan_set_id: planSetId, page_number: pageNumber, rotation, crop },
        done,
      )
    } else {
      router.post(
        sheetsUrl,
        {
          page_number: pageNumber,
          label,
          rotation,
          ...(plans.length > 0 ? { plan_id: planId } : {}),
          ...(cropping && crop ? { crop } : {}),
        },
        done,
      )
    }
  }

  const addingToExisting = cropping && destination !== "new"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size={cropping ? "lg" : "sm"}>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>
              {existing ? `Edit “${existing.label}”` : `Promote page ${pageNumber}`}
            </DialogTitle>
            <DialogDescription>
              {existing
                ? "Rename the sheet or rotate it upright. Rotating re-processes the tiles."
                : "Label this page and rotate it upright if it's sideways — then it becomes a measurable sheet."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* The preview grows to near the thumbnail's native size while
                cropping — a 160px strip is too small to crop accurately. */}
            <div
              ref={containerRef}
              className={`relative flex items-center justify-center overflow-hidden rounded-md border border-hairline bg-surface ${
                cropping ? "h-[min(60vh,30rem)]" : "h-48"
              }`}
              style={cropping ? { cursor: "crosshair", touchAction: "none" } : undefined}
              onPointerDown={onSurfacePointerDown}
              onPointerMove={onSurfacePointerMove}
              onPointerUp={onSurfacePointerUp}
            >
              {thumbUrl ? (
                // The zoom/pan wrapper — the img keeps its own rotate (and its
                // rotate transition), so the two transforms never fight.
                <div
                  className="flex items-center justify-center"
                  style={{ transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.s})` }}
                >
                  <img
                    ref={imgRef}
                    src={src ?? thumbUrl}
                    alt={`Page ${pageNumber} preview`}
                    className={
                      cropping
                        ? "max-h-[min(56vh,28rem)] max-w-[min(56vh,28rem)] transition-transform duration-200"
                        : "max-h-40 max-w-40 transition-transform duration-200"
                    }
                    style={{ transform: `rotate(${rotation}deg)` }}
                    draggable={false}
                  />
                </div>
              ) : (
                <span className="text-sm text-ink-muted">No preview yet</span>
              )}
              {cropping && crop && (
                <div
                  className="pointer-events-none absolute border-2 border-accent bg-accent/15"
                  style={cropSelectionStyle() ?? undefined}
                  data-testid="crop-selection"
                />
              )}
              {cropping && (
                <div
                  className="absolute right-2 top-2 z-10 flex items-center gap-0.5 rounded-lg border border-hairline bg-page/95 px-1 py-0.5 shadow-sm"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="!h-6 !w-6"
                    aria-label="Zoom out"
                    data-testid="crop-zoom-out"
                    onClick={() => zoomAboutCenter(1 / 1.5)}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </Button>
                  <span className="min-w-10 text-center text-xs tabular-nums text-ink-muted">
                    {Math.round(view.s * 100)}%
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="!h-6 !w-6"
                    aria-label="Zoom in"
                    data-testid="crop-zoom-in"
                    onClick={() => zoomAboutCenter(1.5)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="!h-6 px-1.5 text-xs"
                    data-testid="crop-zoom-fit"
                    onClick={fitView}
                  >
                    Fit
                  </Button>
                </div>
              )}
            </div>
            {cropping && (
              <p className="text-center text-xs text-ink-muted">
                Drag to crop · scroll to pan · pinch or ⌘/Ctrl-scroll to zoom
              </p>
            )}
            <div className="flex items-center justify-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setRotation((r) => (r + 270) % 360)
                  setCrop(null)
                  fitView()
                }}
              >
                <RotateCcw className="h-4 w-4" /> Rotate left
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setRotation((r) => (r + 90) % 360)
                  setCrop(null)
                  fitView()
                }}
              >
                <RotateCw className="h-4 w-4" /> Rotate right
              </Button>
            </div>
            {!existing && thumbUrl && (
              <label className="flex items-center gap-2 font-normal text-ink-body">
                <Checkbox
                  checked={cropping}
                  onChange={(e) => {
                    setCropping(e.target.checked)
                    setCrop(null)
                    setDestination("new")
                    fitView()
                  }}
                  data-testid="crop-toggle"
                />
                Crop this page — drag a box over the preview
              </label>
            )}
            {cropping && compositeSheets.length > 0 && (
              <div className="space-y-2">
                <label htmlFor={`crop-destination-${pageNumber}`}>Add the crop to</label>
                <Select
                  id={`crop-destination-${pageNumber}`}
                  value={destination}
                  onChange={(e) =>
                    setDestination(e.target.value === "new" ? "new" : Number(e.target.value))
                  }
                >
                  <option value="new">A new sheet</option>
                  {compositeSheets.map((sheet) => (
                    <option key={sheet.id} value={sheet.id}>
                      {sheet.label} (existing)
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {!addingToExisting && plans.length > 0 && (
              <div className="space-y-2">
                <label htmlFor={`sheet-plan-${pageNumber}`}>Into plan</label>
                <Select
                  id={`sheet-plan-${pageNumber}`}
                  value={planId}
                  onChange={(e) => setPlanId(e.target.value === "" ? "" : Number(e.target.value))}
                >
                  <option value="">Choose a plan…</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.display_name}
                    </option>
                  ))}
                </Select>
                {errors.plan_id && <p className="text-xs text-danger-display">{errors.plan_id}</p>}
              </div>
            )}
            {!addingToExisting && (
            <div className="space-y-2">
              <label htmlFor={`sheet-label-${pageNumber}`}>Sheet label</label>
              <Input
                id={`sheet-label-${pageNumber}`}
                type="text"
                required
                placeholder="1st Floor"
                aria-invalid={!!errors.label}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
              {errors.label && <p className="text-xs text-danger-display">{errors.label}</p>}
              {errors.page_number && (
                <p className="text-xs text-danger-display">{errors.page_number}</p>
              )}
              {errors.rotation && (
                <p className="text-xs text-danger-display">{errors.rotation}</p>
              )}
            </div>
            )}
            {rotationChanged && (
              <p className="text-xs text-ink-muted">
                Rotation changed — the sheet will re-process before it can be opened.
              </p>
            )}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={busy || (cropping && !crop) || (plans.length > 0 && !addingToExisting && planId === "")}>
              {existing
                ? "Save"
                : addingToExisting
                  ? "Add crop to sheet"
                  : cropping
                    ? "Promote cropped region"
                    : "Promote to sheet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
